/**
 * Shared Location Form Modal
 *
 * Generic modal for creating/editing locations that works for both vendors and customers.
 *
 * board2-#79 「地址格式改成一個textfield，丟到googlet拆分」
 * (page 2cb89adb-b34d-80ee-a729-d094a38c45cc): address entry is
 * country (CascadingLocationFields) + postcode + ONE full-address textfield
 * (+ notes). State/city Selects are gone — stateId/cityId stay in the form
 * data only as backend-parse OUTPUTS (Google Maps split, simulated by the
 * mocks); the postcode lookup may pre-resolve them, a country change clears
 * them.
 *
 * Key differences handled via entityType prop:
 * - Vendor: Uses isDefaultReceiving (single toggle)
 * - Customer: Uses isDefaultShipping AND isDefaultBilling (two toggles)
 * - Translation namespace (vendors vs customers)
 * - addressLang handling for customer (langOverride)
 */

import { useEffect, useMemo, useState } from 'react';
import { Modal, Input, Button, Typography, Radio, Row, Col, theme, Skeleton } from 'antd';
import { useForm, Controller } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { controlHeightTokens, radiusTokens } from '@/styles/tokens';
import { useFormStyles } from '@/styles/formStyles';
import { useTranslation } from 'react-i18next';
import { usePostcodeLookup } from '@/hooks/usePostcodeLookup';
import { useCountriesData } from '@/hooks/useLocations';
import { CascadingLocationFields } from '@/components/common/form/CascadingLocationFields';
import {
  vendorLocationFormSchema,
  customerLocationFormSchema,
} from './locationFormSchemas';

const { TextArea } = Input;

/** Supported entity types */
export type LocationEntityType = 'vendor' | 'customer';

/**
 * Vendor location form data - uses isDefaultReceiving
 */
export interface VendorLocationFormData {
  locationName: string;
  isDefaultReceiving: boolean;
  country: string;
  countryId: number | null;
  stateId: number | null;
  state: string;
  cityId: number | null;
  city: string;
  postcode: string;
  address: string;
  addressLang?: string | null;
  notes?: string;
}

/**
 * Customer location form data - uses isDefaultShipping and isDefaultBilling
 */
export interface CustomerLocationFormData {
  locationName: string;
  isDefaultShipping: boolean;
  isDefaultBilling: boolean;
  country: string;
  countryId: number | null;
  stateId: number | null;
  state: string;
  cityId: number | null;
  city: string;
  postcode: string;
  address: string;
  addressLang?: string | null;
  notes?: string;
}

/** Union type for both location form data types */
export type LocationFormData = VendorLocationFormData | CustomerLocationFormData;

/** Default values for vendor locations */
const defaultVendorLocationValues: VendorLocationFormData = {
  locationName: '',
  isDefaultReceiving: false,
  country: '',
  countryId: null,
  stateId: null,
  state: '',
  cityId: null,
  city: '',
  postcode: '',
  address: '',
  addressLang: null,
  notes: '',
};

/** Default values for customer locations */
const defaultCustomerLocationValues: CustomerLocationFormData = {
  locationName: '',
  isDefaultShipping: false,
  isDefaultBilling: false,
  country: '',
  countryId: null,
  stateId: null,
  state: '',
  cityId: null,
  city: '',
  postcode: '',
  address: '',
  addressLang: null,
  notes: '',
};

/**
 * Get translation keys based on entity type
 * Maps generic keys to entity-specific translation paths
 */
function getTranslationKeys(entityType: LocationEntityType) {
  const prefix = entityType === 'vendor' ? 'vendor' : 'customer';
  return {
    // Sections
    basicInformation: `${prefix}.sections.basicInformation`,
    // Fields
    locationName: `${prefix}.fields.locationName`,
    defaultReceiving: 'vendor.fields.defaultReceiving',
    defaultShipping: 'customer.fields.defaultShipping',
    defaultBilling: 'customer.fields.defaultBilling',
    postcode: `${prefix}.fields.postcode`,
    address: `${prefix}.fields.address`,
    notes: `${prefix}.fields.notes`,
    yes: `${prefix}.fields.yes`,
    no: `${prefix}.fields.no`,
    // Placeholders
    locationNamePlaceholder: `${prefix}.placeholders.locationName`,
    postcodePlaceholder: `${prefix}.placeholders.postcode`,
    addressPlaceholder: `${prefix}.placeholders.address`,
    notesPlaceholder: `${prefix}.placeholders.notes`,
    // Validation
    locationNameRequired: `${prefix}.validation.locationNameRequired`,
    postcodeRequired: `${prefix}.validation.postcodeRequired`,
    addressRequired: `${prefix}.validation.addressRequired`,
    // Actions
    close: `${prefix}.actions.close`,
    cancel: `${prefix}.actions.cancel`,
    save: `${prefix}.actions.save`,
    add: `${prefix}.modals.add`,
    // Modal titles
    viewLocation: `${prefix}.modals.viewLocation`,
    editLocation: `${prefix}.modals.editLocation`,
    addLocation: `${prefix}.modals.addLocation`,
  };
}

export interface SharedLocationFormModalProps {
  /** Modal visibility */
  visible: boolean;
  /** Close handler */
  onClose: () => void;
  /** Success handler - receives the form data, can return Promise for loading state */
  onSuccess: (location: LocationFormData) => void | Promise<void>;
  /** Location data for edit mode (null for create mode) */
  location?: LocationFormData | null;
  /** When true, displays form in read-only mode (view mode) */
  readOnly?: boolean;
  /** Entity type - determines translation namespace and field layout */
  entityType: LocationEntityType;
  /** When true, shows a loading spinner instead of form content */
  loading?: boolean;
}

const SharedLocationFormModal = ({
  visible,
  onClose,
  onSuccess,
  location,
  readOnly = false,
  entityType,
  loading = false,
}: SharedLocationFormModalProps) => {
  const { token } = theme.useToken();
  const namespace = entityType === 'vendor' ? 'vendors' : 'customers';
  const { t } = useTranslation(namespace);
  const formStyles = useFormStyles();
  const [saving, setSaving] = useState(false);
  const isEditMode = !!location;
  const keys = getTranslationKeys(entityType);

  // Select schema and default values based on entity type
  const schema = entityType === 'vendor' ? vendorLocationFormSchema : customerLocationFormSchema;
  const defaultValues = entityType === 'vendor' ? defaultVendorLocationValues : defaultCustomerLocationValues;

  // Read-only input style
  const readOnlyStyle = readOnly
    ? {
        backgroundColor: token.colorBgContainerDisabled,
        cursor: 'default',
      }
    : {};

  // Compute form values using useMemo
  const formValues = useMemo(() => {
    if (!visible) {
      return defaultValues;
    }
    return location || defaultValues;
  }, [visible, location, defaultValues]);

  const {
    control,
    handleSubmit,
    watch,
    setValue,
    reset,
    formState: { errors, touchedFields, isSubmitted },
  } = useForm<LocationFormData>({
    resolver: zodResolver(schema),
    mode: 'onTouched',
    reValidateMode: 'onChange',
    defaultValues: defaultValues as LocationFormData,
    values: formValues,
  });

  // Reset form state (errors, isSubmitted) when modal opens
  // The `values` prop syncs form values, but doesn't reset validation state
  useEffect(() => {
    if (visible) {
      reset(formValues);
    }
  }, [visible, reset, formValues]);

  // Watch countryId for postcode lookup and get countries list
  const selectedCountryId = watch('countryId');
  const { countries } = useCountriesData();

  // Resolve countryCode from countryId for postcode lookup
  const selectedCountryCode = useMemo(() => {
    if (!selectedCountryId || !countries) return undefined;
    const country = countries.find((c) => c.countryId === selectedCountryId);
    return country?.countryCode;
  }, [selectedCountryId, countries]);

  // Postcode lookup hook — pre-resolves the state/city PARSE OUTPUTS when the
  // postcode is on the seeded dataset (board2-#79); unresolved rows are left
  // for the backend parse on save
  const { handlePostcodeChange, handlePostcodeBlur } = usePostcodeLookup({
    countryCode: selectedCountryCode,
    setValue: setValue as (name: string, value: unknown) => void,
    stateIdField: 'stateId',
    stateLabelField: 'state',
    cityIdField: 'cityId',
    cityLabelField: 'city',
  });

  const onSubmit = async (data: LocationFormData) => {
    setSaving(true);
    try {
      await onSuccess(data);
    } finally {
      setSaving(false);
    }
  };

  // Footer buttons
  const footerButtons = readOnly ? (
    <div className="tw-flex tw-justify-end">
      <Button
        onClick={onClose}
        style={{
          height: `${controlHeightTokens.controlHeightXL}px`,
          fontSize: token.fontSizeSM,
          borderRadius: `${radiusTokens.card}px`,
        }}
      >
        {t(keys.close)}
      </Button>
    </div>
  ) : (
    <div className="tw-flex tw-justify-end tw-gap-2">
      <Button
        onClick={onClose}
        style={{
          height: `${controlHeightTokens.controlHeightXL}px`,
          fontSize: token.fontSizeSM,
          borderRadius: `${radiusTokens.card}px`,
        }}
      >
        {t(keys.cancel)}
      </Button>
      <Button
        type="primary"
        htmlType="submit"
        form="location-form"
        loading={saving}
        style={{
          height: `${controlHeightTokens.controlHeightXL}px`,
          fontSize: token.fontSizeSM,
          borderRadius: `${radiusTokens.card}px`,
        }}
      >
        {isEditMode ? t(keys.save) : t(keys.add)}
      </Button>
    </div>
  );

  // Modal title based on mode
  const modalTitle = readOnly
    ? t(keys.viewLocation)
    : isEditMode
      ? t(keys.editLocation)
      : t(keys.addLocation);

  return (
    <Modal
      open={visible}
      onCancel={onClose}
      title={modalTitle}
      footer={footerButtons}
      width={800}
      destroyOnHidden
      styles={{
        body: { maxHeight: 'calc(100vh - 200px)', overflowY: 'auto' },
      }}
    >
      <style>
        {`
          .location-form-modal .ant-select-selector {
            height: ${controlHeightTokens.controlHeightXL}px !important;
            display: flex !important;
            align-items: center !important;
          }
          .ant-select-dropdown .ant-select-item {
            min-height: ${controlHeightTokens.controlHeightXL}px !important;
            padding: 10px 12px !important;
            font-size: ${token.fontSize}px !important;
            line-height: ${controlHeightTokens.controlHeightXL - 20}px !important;
            display: flex !important;
            align-items: center !important;
          }
        `}
      </style>
      {loading ? (
        <div style={{ padding: '8px 0' }}>
          {/* Basic Information Section Skeleton */}
          <div style={formStyles.fieldContainerStyle}>
            <Skeleton.Input active size="small" style={{ width: 120, marginBottom: 12 }} />
            <Row gutter={12}>
              <Col span={16}>
                <Skeleton.Input active size="small" style={{ width: 100, marginBottom: 4 }} />
                <Skeleton.Input active size="large" style={{ width: '100%' }} />
              </Col>
              <Col span={8}>
                <Skeleton.Input active size="small" style={{ width: 80, marginBottom: 4 }} />
                <Skeleton.Input active size="large" style={{ width: '100%' }} />
              </Col>
            </Row>
            {/* Country Row (board2-#79 single-address entry group) */}
            <Row gutter={12} style={{ marginTop: 12 }}>
              <Col span={8}><Skeleton.Input active size="large" style={{ width: '100%' }} /></Col>
            </Row>
            {/* Postcode Row */}
            <Row gutter={12} style={{ marginTop: 12 }}>
              <Col span={8}><Skeleton.Input active size="large" style={{ width: '100%' }} /></Col>
            </Row>
            {/* Full-address Row */}
            <Row gutter={12} style={{ marginTop: 12 }}>
              <Col span={24}><Skeleton.Input active size="large" style={{ width: '100%' }} /></Col>
            </Row>
            {/* Notes Row */}
            <Row gutter={12} style={{ marginTop: 12 }}>
              <Col span={24}>
                <Skeleton.Input active size="small" style={{ width: 40, marginBottom: 4 }} />
                <Skeleton.Input active size="large" style={{ width: '100%', height: 80 }} />
              </Col>
            </Row>
          </div>
        </div>
      ) : (
        <form
          id="location-form"
          className="location-form-modal"
          onSubmit={(e) => {
            e.preventDefault();
            handleSubmit(onSubmit)(e);
          }}
        >
        {/* Basic Information Container */}
        <div style={formStyles.fieldContainerStyle}>
          <Typography.Text strong style={formStyles.labelStyle}>
            {t(keys.basicInformation)}
          </Typography.Text>

          {/* Row 1: Location Name + Default toggles (differs by entity type) */}
          {entityType === 'vendor' ? (
            // Vendor: Location Name (16) + Default Receiving (8)
            <Row gutter={12}>
              <Col span={16}>
                <Typography.Text style={{ fontSize: token.fontSizeSM, color: token.colorTextSecondary }}>
                  <span style={{ color: token.colorError, marginRight: '4px' }}>*</span>
                  {t(keys.locationName)}
                </Typography.Text>
                <Controller
                  name="locationName"
                  control={control}
                  render={({ field }) => (
                    <>
                      <Input
                        size="large"
                        {...field}
                        readOnly={readOnly}
                        placeholder={t(keys.locationNamePlaceholder)}
                        style={{
                          ...formStyles.inputStyle,
                          ...readOnlyStyle,
                          marginTop: '4px',
                          borderColor: errors.locationName ? token.colorError : undefined,
                        }}
                      />
                      {!readOnly && errors.locationName && (touchedFields.locationName || isSubmitted) && (
                        <Typography.Text style={{ fontSize: token.fontSizeSM, color: token.colorError }}>
                          {t(errors.locationName.message!)}
                        </Typography.Text>
                      )}
                    </>
                  )}
                />
              </Col>
              <Col span={8}>
                <Typography.Text style={{ fontSize: token.fontSizeSM, color: token.colorTextSecondary }}>
                  {t(keys.defaultReceiving)}
                </Typography.Text>
                <Controller
                  name={'isDefaultReceiving' as keyof LocationFormData}
                  control={control}
                  render={({ field }) => (
                    <Radio.Group
                      value={field.value as unknown as boolean}
                      onChange={(e) => !readOnly && field.onChange(e.target.value)}
                      className={readOnly ? 'form-readonly-radio' : undefined}
                      style={{
                        display: 'flex',
                        alignItems: 'center',
                        height: `${controlHeightTokens.controlHeightXL}px`,
                        marginTop: '4px',
                      }}
                    >
                      <Radio value={true}>{t(keys.yes)}</Radio>
                      <Radio value={false}>{t(keys.no)}</Radio>
                    </Radio.Group>
                  )}
                />
              </Col>
            </Row>
          ) : (
            // Customer: Location Name (10) + Default Shipping (7) + Default Billing (7)
            <Row gutter={12}>
              <Col span={10}>
                <Typography.Text style={{ fontSize: token.fontSizeSM, color: token.colorTextSecondary }}>
                  <span style={{ color: token.colorError, marginRight: '4px' }}>*</span>
                  {t(keys.locationName)}
                </Typography.Text>
                <Controller
                  name="locationName"
                  control={control}
                  render={({ field }) => (
                    <>
                      <Input
                        size="large"
                        {...field}
                        readOnly={readOnly}
                        placeholder={t(keys.locationNamePlaceholder)}
                        style={{
                          ...formStyles.inputStyle,
                          ...readOnlyStyle,
                          marginTop: '4px',
                          borderColor: errors.locationName ? token.colorError : undefined,
                        }}
                      />
                      {!readOnly && errors.locationName && (touchedFields.locationName || isSubmitted) && (
                        <Typography.Text style={{ fontSize: token.fontSizeSM, color: token.colorError }}>
                          {t(errors.locationName.message!)}
                        </Typography.Text>
                      )}
                    </>
                  )}
                />
              </Col>
              <Col span={7}>
                <Typography.Text style={{ fontSize: token.fontSizeSM, color: token.colorTextSecondary }}>
                  <span style={{ color: token.colorError, marginRight: '4px' }}>*</span>
                  {t(keys.defaultShipping)}
                </Typography.Text>
                <Controller
                  name={'isDefaultShipping' as keyof LocationFormData}
                  control={control}
                  render={({ field }) => (
                    <Radio.Group
                      value={field.value as unknown as boolean}
                      onChange={(e) => !readOnly && field.onChange(e.target.value)}
                      className={readOnly ? 'form-readonly-radio' : undefined}
                      style={{
                        display: 'flex',
                        alignItems: 'center',
                        height: `${controlHeightTokens.controlHeightXL}px`,
                        marginTop: '4px',
                      }}
                    >
                      <Radio value={true}>{t(keys.yes)}</Radio>
                      <Radio value={false}>{t(keys.no)}</Radio>
                    </Radio.Group>
                  )}
                />
              </Col>
              <Col span={7}>
                <Typography.Text style={{ fontSize: token.fontSizeSM, color: token.colorTextSecondary }}>
                  <span style={{ color: token.colorError, marginRight: '4px' }}>*</span>
                  {t(keys.defaultBilling)}
                </Typography.Text>
                <Controller
                  name={'isDefaultBilling' as keyof LocationFormData}
                  control={control}
                  render={({ field }) => (
                    <Radio.Group
                      value={field.value as unknown as boolean}
                      onChange={(e) => !readOnly && field.onChange(e.target.value)}
                      className={readOnly ? 'form-readonly-radio' : undefined}
                      style={{
                        display: 'flex',
                        alignItems: 'center',
                        height: `${controlHeightTokens.controlHeightXL}px`,
                        marginTop: '4px',
                      }}
                    >
                      <Radio value={true}>{t(keys.yes)}</Radio>
                      <Radio value={false}>{t(keys.no)}</Radio>
                    </Radio.Group>
                  )}
                />
              </Col>
            </Row>
          )}

          {/* Country Select (single-address entry group, countryId mode) —
              state/city field names are passed ONLY so a country change clears
              the stale parse outputs (board2-#79) */}
          <div style={{ marginTop: '12px' }}>
            <CascadingLocationFields
              control={control}
              errors={errors}
              watch={watch}
              setValue={setValue}
              countryFieldName="countryId"
              countryLabelFieldName="country"
              useCountryId={true}
              stateFieldName="stateId"
              stateLabelFieldName="state"
              cityFieldName="cityId"
              cityLabelFieldName="city"
              countryRequired={true}
              readOnly={readOnly}
              langOverride={location?.addressLang}
            />
          </div>

          {/* Row: Postcode (the lookup input feeding the parse simulation) */}
          <Row gutter={12} style={{ marginTop: '12px' }}>
            <Col span={8}>
              <Typography.Text style={{ fontSize: token.fontSizeSM, color: token.colorTextSecondary }}>
                <span style={{ color: token.colorError, marginRight: '4px' }}>*</span>
                {t(keys.postcode)}
              </Typography.Text>
              <Controller
                name="postcode"
                control={control}
                render={({ field }) => (
                  <>
                    <Input
                      size="large"
                      {...field}
                      readOnly={readOnly}
                      onChange={(e) => {
                        field.onChange(e);
                        if (!readOnly) handlePostcodeChange(e.target.value);
                      }}
                      onBlur={() => {
                        field.onBlur();
                        if (!readOnly) handlePostcodeBlur(String(field.value ?? ''));
                      }}
                      placeholder={t(keys.postcodePlaceholder)}
                      style={{
                        ...formStyles.inputStyle,
                        ...readOnlyStyle,
                        marginTop: '4px',
                        borderColor: errors.postcode ? token.colorError : undefined,
                      }}
                    />
                    {!readOnly && errors.postcode && (touchedFields.postcode || isSubmitted) && (
                      <Typography.Text style={{ fontSize: token.fontSizeSM, color: token.colorError }}>
                        {t(errors.postcode.message!)}
                      </Typography.Text>
                    )}
                  </>
                )}
              />
            </Col>
          </Row>

          {/* Row: single full-address textfield — the whole street address in
              ONE field; the backend splits it into stateId/cityId (board2-#79) */}
          <Row gutter={12} style={{ marginTop: '12px' }}>
            <Col span={24}>
              <Typography.Text style={{ fontSize: token.fontSizeSM, color: token.colorTextSecondary }}>
                <span style={{ color: token.colorError, marginRight: '4px' }}>*</span>
                {t(keys.address)}
              </Typography.Text>
              <Controller
                name="address"
                control={control}
                render={({ field }) => (
                  <>
                    <Input
                      size="large"
                      {...field}
                      readOnly={readOnly}
                      placeholder={t(keys.addressPlaceholder)}
                      style={{
                        ...formStyles.inputStyle,
                        ...readOnlyStyle,
                        marginTop: '4px',
                        borderColor: errors.address ? token.colorError : undefined,
                      }}
                    />
                    {!readOnly && errors.address && (touchedFields.address || isSubmitted) && (
                      <Typography.Text style={{ fontSize: token.fontSizeSM, color: token.colorError }}>
                        {t(errors.address.message!)}
                      </Typography.Text>
                    )}
                  </>
                )}
              />
            </Col>
          </Row>

          {/* Notes */}
          <Row gutter={12} style={{ marginTop: '12px' }}>
            <Col span={24}>
              <Typography.Text style={{ fontSize: token.fontSizeSM, color: token.colorTextSecondary }}>
                {t(keys.notes)}
              </Typography.Text>
              <Controller
                name="notes"
                control={control}
                render={({ field }) => (
                  <TextArea
                    {...field}
                    size="large"
                    readOnly={readOnly}
                    placeholder={t(keys.notesPlaceholder)}
                    rows={3}
                    style={{
                      marginTop: '4px',
                      borderRadius: `${radiusTokens.card}px`,
                      ...readOnlyStyle,
                    }}
                  />
                )}
              />
            </Col>
          </Row>
        </div>
        </form>
      )}
    </Modal>
  );
};

export default SharedLocationFormModal;
export { SharedLocationFormModal };
